import React from 'react'
import { Drawer, Descriptions, Divider, Space, Button, Tag, Typography, Avatar, Image } from 'antd'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { Product } from '../types/product.types'
import { DocumentImage } from '../../../shared/components/DocumentImage'

const { Text } = Typography

interface ProductDetailsDrawerProps {
    open: boolean
    onClose: () => void
    product: Product | null
    onEdit: (product: Product) => void
    onDelete: (product: Product) => void
}

export const ProductDetailsDrawer: React.FC<ProductDetailsDrawerProps> = ({
    open,
    onClose,
    product,
    onEdit,
    onDelete
}) => {
    if (!product) return null

    return (
        <Drawer
            title="Product Details"
            placement="right"
            width={600}
            onClose={onClose}
            open={open}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    {product.imageId ? (
                        <Space direction="vertical" size="middle" style={{ width: '100%', alignItems: 'center' }}>
                            <Image.PreviewGroup>
                                <DocumentImage
                                    documentId={product.imageId}
                                    width={200}
                                    height={200}
                                    style={{ objectFit: 'cover', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                            </Image.PreviewGroup>

                            {product.images && product.images.length > 1 && (
                                <Space size="small" wrap style={{ width: '100%', justifyContent: 'center' }}>
                                    <Image.PreviewGroup>
                                        {product.images.map((img, index) => (
                                            <DocumentImage
                                                key={index}
                                                documentId={img}
                                                width={60}
                                                height={60}
                                                style={{
                                                    objectFit: 'cover',
                                                    borderRadius: 6,
                                                    cursor: 'pointer',
                                                    border: img === product.imageId ? '2px solid #1890ff' : '1px solid #d9d9d9'
                                                } as React.CSSProperties}
                                            />
                                        ))}
                                    </Image.PreviewGroup>
                                </Space>
                            )}
                        </Space>
                    ) : product.imageUrl ? (
                        <Image
                            src={product.imageUrl}
                            width={200}
                            height={200}
                            style={{ objectFit: 'cover', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        />
                    ) : (
                        <Avatar size={120} shape="square" style={{ backgroundColor: '#f5f5f5', color: '#bfbfbf', fontSize: 32 }}>
                            {product.name.charAt(0)}
                        </Avatar>
                    )}
                </div>
                <Descriptions title="Product Information" column={1} bordered>
                    <Descriptions.Item label="Product Name">
                        <Text strong>{product.name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="SKU">{product.sku}</Descriptions.Item>
                    <Descriptions.Item label="Category">
                        <Tag color="blue">{product.category}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Brand">{product.brand}</Descriptions.Item>
                    <Descriptions.Item label="Default Unit">{product.unit}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag
                            color={
                                product.status === 'active'
                                    ? 'green'
                                    : product.status === 'inactive'
                                        ? 'gray'
                                        : 'red'
                            }
                        >
                            {product.status.replace('_', ' ').toUpperCase()}
                        </Tag>
                    </Descriptions.Item>
                </Descriptions>

                {product.specifications && product.specifications.length > 0 && (
                    <>
                        <Divider orientation="left">Specifications</Divider>
                        <Descriptions column={1} size="small" bordered>
                            {product.specifications.map((spec, index) => (
                                <Descriptions.Item key={index} label={spec.key}>
                                    {spec.value}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                    </>
                )}

                {product.description && (
                    <>
                        <Divider orientation="left">Description</Divider>
                        <Text>{product.description}</Text>
                    </>
                )}

                {product.warehouses && product.warehouses.length > 0 && (
                    <>
                        <Divider orientation="left">Origin Locations</Divider>
                        <Descriptions column={1} size="small" bordered>
                            {product.warehouses.map((wh, index) => (
                                <Descriptions.Item key={index} label={wh.type === 'warehouse' ? 'Warehouse' : 'Manufacturing Unit'}>
                                    <Text strong>{wh.name}</Text>
                                    <br />
                                    <Text type="secondary">Coordinates: {wh.latitude}, {wh.longitude}</Text>
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                    </>
                )}

                <Divider />

                <Space>
                    <Button type="primary" icon={<EditOutlined />} onClick={() => onEdit(product)}>
                        Edit Product
                    </Button>
                    <Button danger icon={<DeleteOutlined />} onClick={() => onDelete(product)}>
                        Delete Product
                    </Button>
                </Space>
            </Space>
        </Drawer>
    )
}
